import { useState, useEffect } from "react";
import axios from "axios";
import { Bank, Phone, FloppyDisk, Check, Wallet } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_CURRENCY = {
  UG: "UGX", KE: "KES", NG: "NGN", GH: "GHS", ZA: "ZAR", RW: "RWF", TZ: "TZS",
  ZM: "ZMW", US: "USD", GB: "GBP",
};

export default function PayoutSettings({ compact = false }) {
  const [payout, setPayout] = useState(null);
  const [method, setMethod] = useState("mobile_money");
  const [form, setForm] = useState({
    account_name: "", account_currency: "UGX", country: "UG",
    bank_name: "", bank_country: "UG", account_number: "", swift_code: "",
    network: "MTN", phone_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/me/payout`, { withCredentials: true })
      .then((r) => {
        const p = r.data?.payout || {};
        if (p.method) {
          setMethod(p.method);
          setForm((f) => ({ ...f, ...p }));
          setPayout(p);
        }
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setError(""); setSaving(true); setSaved(false);
    try {
      const payload = { method, ...form };
      const r = await axios.put(`${API}/me/payout`, payload, { withCredentials: true });
      setPayout(r.data.payout);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className={`bg-white border border-stone-200 rounded-2xl p-5 ${compact ? "" : "max-w-2xl"}`} data-testid="payout-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
          <Wallet size={18} weight="duotone" className="text-emerald-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-base font-semibold text-stone-900">Payout Details</h3>
          <p className="text-[11px] text-stone-500">Where to send your earnings from carbon credit sales.</p>
        </div>
        {payout && (
          <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            Saved
          </span>
        )}
      </div>

      {/* Method toggle */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          onClick={() => setMethod("mobile_money")}
          data-testid="payout-method-mm"
          className={`text-xs py-2 rounded-lg border flex items-center justify-center gap-1.5 ${
            method === "mobile_money" ? "bg-emerald-700 text-white border-emerald-800" : "border-stone-200 text-stone-600 hover:border-emerald-400"
          }`}
        >
          <Phone size={14} weight="fill" /> Mobile Money
        </button>
        <button
          onClick={() => setMethod("bank")}
          data-testid="payout-method-bank"
          className={`text-xs py-2 rounded-lg border flex items-center justify-center gap-1.5 ${
            method === "bank" ? "bg-emerald-700 text-white border-emerald-800" : "border-stone-200 text-stone-600 hover:border-emerald-400"
          }`}
        >
          <Bank size={14} weight="fill" /> Bank Account
        </button>
      </div>

      <div className="space-y-3">
        <Input label="Account holder name *" value={form.account_name} onChange={(v) => update("account_name", v)} testId="payout-account-name" />
        <div className="grid grid-cols-2 gap-2">
          <Select label="Country" value={form.country} onChange={(v) => { update("country", v); update("account_currency", COUNTRY_CURRENCY[v] || ""); }}
                  options={["UG","KE","NG","GH","ZA","RW","TZ","ZM","US","GB"]} testId="payout-country" />
          <Input label="Currency" value={form.account_currency} onChange={(v) => update("account_currency", v.toUpperCase())} testId="payout-currency" />
        </div>

        {method === "mobile_money" && (
          <>
            <Select label="Mobile money network" value={form.network} onChange={(v) => update("network", v)}
                    options={["MTN","Airtel","M-Pesa","Tigo","Vodacom","Orange","Moov"]} testId="payout-network" />
            <Input label="Phone number * (e.g. +256700000000)" value={form.phone_number} onChange={(v) => update("phone_number", v)} testId="payout-phone" />
          </>
        )}

        {method === "bank" && (
          <>
            <Input label="Bank name *" value={form.bank_name} onChange={(v) => update("bank_name", v)} testId="payout-bank-name" />
            <Input label="Account number *" value={form.account_number} onChange={(v) => update("account_number", v)} testId="payout-account-number" />
            <Input label="SWIFT / BIC code (optional)" value={form.swift_code} onChange={(v) => update("swift_code", v)} testId="payout-swift" />
          </>
        )}
      </div>

      {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">{error}</p>}

      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-stone-400 text-white text-sm font-medium px-5 py-2.5 rounded-full"
          data-testid="payout-save"
        >
          <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save Payout Method"}
        </button>
        {saved && (
          <span className="text-emerald-700 text-xs inline-flex items-center gap-1" data-testid="payout-saved">
            <Check size={14} weight="bold" /> Saved
          </span>
        )}
      </div>

      <p className="text-[10px] text-stone-400 mt-3">
        Sellers must add payout details before they can receive funds from carbon credit sales.
      </p>
    </div>
  );
}

function Input({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-stone-400 mb-1">{label}</label>
      <input
        type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
        data-testid={testId}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-stone-400 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white focus:outline-none focus:border-emerald-500"
        data-testid={testId}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
